import React, { useState, useEffect } from 'react';
import { Button, Box, Modal, TextField, CircularProgress } from '@mui/material';
import InputFieldDataProfile from "../../../components/InputFieldDataProfile";
import editIcon from "../../../img/pencil-icon.svg";
import saveIcon from '../../../img/save-icon.svg';
import cancelIcon from '../../../img/close-icon-button.svg';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { paymentsEmailSchema } from './../../../validations/paymentsEmailValidation';
import { useReSendPaymentVerificationMutation, useVerifyPaymentEmailMutation, useUpdateDataMutation } from '../../../services/accountsApi';
import { createErrorMessage } from '../../../utils';
import { useNotification } from '../../../context/NotificationContext';

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 320,
  padding: '2em',
  bgcolor: 'background.paper',
  borderRadius: '1em',
  boxShadow: 24,
};

function PencilOrangeIconSvg() {
  return <img src={editIcon} alt="edit icon" height="18px" width="18px" />;
}

const PaymentsField = ({ user, setUser }) => {
  const { showNotification } = useNotification();

  // We do NOT save a clone of the entire user, only what we edit:
  const [edit, setEdit] = useState(false);
  const [tempEmail, setTempEmail] = useState(user?.paymentsEmail || "");
  const [error, setError] = useState([]);

  const [verifyCode] = useVerifyPaymentEmailMutation();
  const [reSendCode] = useReSendPaymentVerificationMutation();
  const [initUpdateData, { isLoading: updateDataIsLoading }] = useUpdateDataMutation();

// Control of the verification modal
  const [isVerifiedModal, setIsVerifiedModal] = useState(null);
  const [verificationCode, setVerificationCode] = useState(null);
  const [verificationError, setVerificationError] = useState([]);
  const [verificationProcess, setVerificationProcess] = useState(false);
  const [timer, setTimer] = useState(60);
  const [isActiveTimer, setIsActiveTimer] = useState(false);

  // Every time Redux updates the user (for example, with a new paymentsEmail),
  // we sync the tempEmail if we are NOT in edit mode, to avoid overwriting
  // ongoing changes.
  useEffect(() => {
    if (!edit) {
      setTempEmail(user.paymentsEmail || "");
    }
  }, [user, edit]);

  const openEditForm = () => {
    setError([]);
    setTempEmail(user.paymentsEmail || ""); 
    setEdit(true);
  };

  const closeEditForm = () => {
    setError([]);
    setTempEmail(user.paymentsEmail || "");
    setEdit(false);
  };

  async function sendData() {
    try {
      await paymentsEmailSchema.validate(tempEmail);
      setError([]);

      // If the email really changed, we send it to the server.
      if (tempEmail !== user.paymentsEmail) {
        const updatedBody = { ...user, paymentsEmail: tempEmail };
        let result = await initUpdateData({ id: user.id, body: updatedBody }).unwrap();
        
        // We update in Redux
        setUser({
          ...user,
          paymentsEmail: result.paymentsEmail,
          paymentsEmailVerified: result.paymentsEmailVerified
        });

        openModalWindow(); // We open the verification modal (if necessary)
      }
      
      closeEditForm();
    } catch (err) {
      if (err.name === "ValidationError") {
        setError(err.errors);
      } else {
        setError([err.message]);
      }
    }
  }

  // Verification modal logic:
  const onVerifiedCodeSubmited = async () => {
    setVerificationProcess(true);
    try {
      await verifyCode(verificationCode).unwrap();
      setUser({
        ...user,
        paymentsEmailVerified: true
      });
    } catch (error) {
      showNotification(createErrorMessage(error), "error");
    } finally {
      closeModalWindow();
      setVerificationProcess(false);
    }
  };

  const sendNewCodeClick = () => {
    reSendCode();
    setIsActiveTimer(true);
  };

  useEffect(() => {
    let intervalId;
    if (isActiveTimer) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 0) {
            clearInterval(intervalId);
            setIsActiveTimer(false);
            return 60;
          } else {
            return prevTimer - 1;
          }
        });
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [isActiveTimer]);

  const openModalWindow = () => {
    setIsVerifiedModal({ directionEmail: tempEmail });
    setVerificationCode(null);
    setVerificationProcess(false);
    setVerificationError([]);
    setIsActiveTimer(false);
  };

  const closeModalWindow = () => {
    setIsVerifiedModal(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
        
        {/* Input with its label */}
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <InputFieldDataProfile
            label={
              <Box sx={{ display: 'flex' }}>
                <span>My PayPal Email</span>
                {user?.paymentsEmail && (
                  <Box className="status-data-card-profile">
                    {user.paymentsEmailVerified ? (
                      <VerifiedIcon color='success' />
                    ) : (
                      <NewReleasesIcon color='error' />
                    )}
                    <span
                      style={{ marginLeft: '8px' }}
                      className={user.paymentsEmailVerified ? "success" : "done"}
                    >
                      {user.paymentsEmailVerified ? "Verified" : "Not Verified"}
                    </span>
                  </Box>
                )}
              </Box>
            }
            // When not in edit mode, we show user.paymentsEmail directly.
            // When in edit mode, we use tempEmail (local state).
            value={edit ? tempEmail : (user.paymentsEmail ?? "")}
            onChange={(e) => {
              setError([]);
              setTempEmail(e.target.value);
            }}
            edit={edit}
          />
        </Box>

        {/* Edit/Save/Cancel buttons */}
        <Box className="edit-button-container">
          {edit ? (
            <div className="container-button-form">
              <Button onClick={sendData}>
                <img src={saveIcon} alt='Save Icon' height='18px' style={{ marginRight: '0.25em' }} />
                Save
              </Button>
              <Button className="btn-cancel-form" onClick={closeEditForm}>
                <img src={cancelIcon} alt='Cancel Icon' height='18px' style={{ marginRight: '0.25em' }} />
                Cancel
              </Button>
            </div>
          ) : (
            <div className="container-button-form">
              {user?.paymentsEmail && !user?.paymentsEmailVerified && (
                <Button onClick={openModalWindow}>
                  <SendIcon />
                  <span style={{ marginLeft: "0.5em" }}>Verify</span>
                </Button>
              )}
              <Button onClick={openEditForm}>
                <PencilOrangeIconSvg />
                <span style={{ marginLeft: "0.5em" }}>Edit</span>
              </Button>
            </div>
          )}
        </Box>
      </Box>

      {/* Validation errors */}
      <Box sx={{ display: "flex" }}>
        {error && error.length > 0 && (
          <ul style={{ color: 'var(--red)' }} >
            {error.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
      </Box>

      {/* Code verification modal */}
      <Modal
        open={Boolean(isVerifiedModal)}
        onClose={closeModalWindow}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={ModalStyle}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box>
              <h4 style={{ color: "var(--orange)" }} >Verification Code</h4>
            </Box>
            <Box sx={{textAlign: 'center', marginTop: '5px', marginBottom: '5px', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <span>
                Please type the verification code send to <b>{isVerifiedModal?.directionEmail}</b>
              </span>
              <TextField
                onChange={e => { setVerificationCode(e.target.value); setVerificationError([]); }}
                sx={{ marginTop: '5px', width: '100px', borderRadius: '50px' }}
                inputProps={{ maxLength: 4, style: { fontSize: 40, margin: 0, padding: 0, paddingLeft: 4 } }}
                InputProps={{ style: { borderRadius: "14px" } }}
                disabled={verificationProcess}
              />
            </Box>
            {verificationError && verificationError.length > 0 && (
              <Box sx={{ display: "flex" }}>
                <ul style={{ margin: 2, color: 'var(--red)' }} >
                  {verificationError.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </Box>
            )}
            <Box>
              <Button
                variant='contained'
                type="submit"
                name="acceptButton"
                color='primary'
                sx={{ minWidth: '100px' }}
                disabled={verificationProcess}
                onClick={onVerifiedCodeSubmited}
              >
                {verificationProcess ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            </Box>
            <Box sx={{ marginTop: '5px' }}>
              {isActiveTimer ? (
                <span className='tx-gray-inactive'>Send a New Code in {timer}</span>
              ) : (
                <span className='cursor-pointer tx-gray-inactive tx-orange-hover' onClick={sendNewCodeClick}>
                  Send a New Code
                </span>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PaymentsField;
